import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import BackButton from "../../../../../components/Button/BackButton";
import FormInput from "../../../../../components/Form/FormInput";
import FormSelect from "../../../../../components/Form/FormSelect";
import FormTextarea from "../../../../../components/Form/TextArea";
import { clientName, formatLanguagePair } from "../../quotationFormat";
import { btn } from "../../../../../components/Button/buttonStyles";

const EMPTY_FORM = {
  service_id: "",
  language_pair_id: "",
  unit_amount: "",
  unit_id: "",
  price_per_unit: "",
  subtotal: "",
  currency_id: "",
  file_id: "",
  internal_note: "",
};

const asId = (v) => (v ? String(v) : "");

// A failed lookup leaves its dropdown empty rather than breaking the page.
const listOrEmpty = (request, pick = (res) => res.data.data) =>
  request.then((res) => (Array.isArray(pick(res)) ? pick(res) : [])).catch(() => []);

// Create and edit for both receivable types; type comes from the query string or the stored row.
const QuotationReceivablePage = () => {
  const navigate = useNavigate();
  const { id: quotationId, receivableId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(receivableId);

  const [type, setType] = useState(searchParams.get("type") === "unit_based" ? "unit_based" : "flat_rate");
  const [form, setForm] = useState(EMPTY_FORM);
  const [meta, setMeta] = useState({ client: "", specialization: "" });
  const [options, setOptions] = useState({
    services: [],
    languagePairs: [],
    currencies: [],
    units: [],
    files: [],
    prices: [],
  });
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const isUnit = type === "unit_based";

  useEffect(() => {
    const load = async () => {
      try {
        const [quotationRes, receivableRes] = await Promise.all([
          api.get(`/quotations/${quotationId}`),
          isEdit ? api.get(`/quotations/${quotationId}/receivables/${receivableId}`) : Promise.resolve(null),
        ]);
        const quotation = quotationRes.data.data;
        const clientId = quotation.client_id;

        setMeta({
          client: clientName(quotation.client) || "—",
          specialization: quotation.specialization?.name || "",
        });

        // Services, pairs and currencies come from the client's price list, as on projects.
        const [services, languagePairs, currencies, units, files, prices] = await Promise.all([
          listOrEmpty(api.get(`/client-price-list/client/${clientId}/services`)),
          listOrEmpty(api.get(`/client-price-list/client/${clientId}/language-pairs`)),
          listOrEmpty(api.get(`/client-price-list/client/${clientId}/currencies`)),
          listOrEmpty(api.get("/admin-units")),
          listOrEmpty(api.get(`/quotations/${quotationId}/files`)),
          listOrEmpty(api.get(`/client-price-list/client/${clientId}/price-list`), (res) => res.data.data?.priceList),
        ]);
        setOptions({ services, languagePairs, currencies, units, files, prices });

        if (receivableRes) {
          const r = receivableRes.data.data;
          setType(r.type);
          setForm({
            service_id: asId(r.service_id),
            language_pair_id: asId(r.language_pair_id),
            unit_amount: r.unit_amount ?? "",
            unit_id: asId(r.unit_id),
            price_per_unit: r.price_per_unit ?? "",
            subtotal: r.subtotal ?? "",
            currency_id: asId(r.currency_id),
            file_id: asId(r.file_id),
            internal_note: r.internal_note || "",
          });
        }
      } catch (err) {
        console.error("Failed to load receivable form", err);
        setServerError(err.response?.data?.message || "Failed to load form data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [quotationId, receivableId, isEdit]);

  // Unit-based subtotal is always amount x price.
  const unitSubtotal = useMemo(() => {
    const qty = Number(form.unit_amount);
    const price = Number(form.price_per_unit);
    return qty && price ? (qty * price).toFixed(2) : "";
  }, [form.unit_amount, form.price_per_unit]);

  const unitName = (unitId) => options.units.find((u) => u.id === Number(unitId))?.name || "";

  // Price-list rows matching what is picked so far float to the top in green.
  const prices = useMemo(() => {
    const matches = (p) =>
      (!form.service_id || p.service_id === Number(form.service_id)) &&
      (!form.language_pair_id || p.language_pair_id === Number(form.language_pair_id)) &&
      (!meta.specialization || p.specialization?.name === meta.specialization) &&
      (!form.unit_id || p.unit === unitName(form.unit_id)) &&
      (!form.currency_id || p.currency_id === Number(form.currency_id));

    const anyCriteria = form.service_id || form.language_pair_id || form.unit_id || form.currency_id;
    const tagged = options.prices.map((p) => ({ ...p, isMatching: Boolean(anyCriteria) && matches(p) }));
    return [...tagged.filter((p) => p.isMatching), ...tagged.filter((p) => !p.isMatching)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.prices, options.units, form.service_id, form.language_pair_id, form.unit_id, form.currency_id, meta.specialization]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setServerError("");
    setSuccess("");
  };

  const handlePriceSelect = (row) => {
    setSelectedPriceId(row.id);
    setForm((prev) => ({
      ...prev,
      service_id: asId(options.services.find((s) => s.id === row.service_id)?.id),
      language_pair_id: asId(options.languagePairs.find((lp) => lp.id === row.language_pair_id)?.id),
      unit_id: asId(options.units.find((u) => u.name === row.unit)?.id),
      price_per_unit: row.price_per_unit || "",
      currency_id: asId(options.currencies.find((c) => c.id === row.currency_id)?.id),
      internal_note: row.note || prev.internal_note,
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.service_id) e.service_id = "Service is required.";
    if (!form.currency_id) e.currency_id = "Currency is required.";
    if (isUnit) {
      if (!(Number(form.unit_amount) > 0)) e.unit_amount = "Enter a unit amount above 0.";
      if (!form.unit_id) e.unit_id = "Unit is required.";
      if (!(Number(form.price_per_unit) > 0)) e.price_per_unit = "Enter a price above 0.";
    } else if (!(Number(form.subtotal) > 0)) {
      e.subtotal = "Enter a subtotal above 0.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    ...(isEdit ? {} : { type }),
    service_id: Number(form.service_id),
    language_pair_id: form.language_pair_id ? Number(form.language_pair_id) : null,
    currency_id: Number(form.currency_id),
    file_id: form.file_id ? Number(form.file_id) : null,
    internal_note: form.internal_note.trim() || null,
    ...(isUnit
      ? {
          unit_amount: Number(form.unit_amount),
          unit_id: Number(form.unit_id),
          price_per_unit: Number(form.price_per_unit),
        }
      : { subtotal: Number(form.subtotal) }),
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = isEdit
        ? await api.put(`/quotations/${quotationId}/receivables/${receivableId}`, buildPayload())
        : await api.post(`/quotations/${quotationId}/receivables`, buildPayload());
      setSuccess(res.data.message || "Receivable saved.");
      setTimeout(() => navigate(`/quotation/${quotationId}?tab=finances`), 1000);
    } catch (err) {
      setServerError(err.response?.data?.message || "Failed to save receivable");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  }

  const title = `${isEdit ? "Edit" : "New"} ${isUnit ? "Unit Based" : "Flat Rate"} Receivable`;

  const fields = (
    <form onSubmit={submit} noValidate className="bg-white shadow rounded-lg p-8 space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase text-gray-500 font-semibold mb-1">Quotation Client</p>
          <p className="text-gray-900 font-medium">{meta.client}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-gray-500 font-semibold mb-1">Quotation Specialization</p>
          <p className="text-gray-900 font-medium">{meta.specialization || "—"}</p>
        </div>
      </div>

      <hr className="border-gray-200" />

      <div className={`grid grid-cols-1 gap-x-5 gap-y-4 ${isUnit ? "" : "md:grid-cols-2"}`}>
        <FormSelect
          label="Service"
          name="service_id"
          value={form.service_id}
          onChange={handleChange}
          options={options.services.map((s) => ({ value: s.id, label: s.name }))}
          error={errors.service_id}
          required
        />

        <FormSelect
          label="Language Pair"
          name="language_pair_id"
          value={form.language_pair_id}
          onChange={handleChange}
          options={options.languagePairs.map((lp) => ({ value: lp.id, label: formatLanguagePair(lp) }))}
        />

        {isUnit && (
          <>
            <FormInput
              label="Unit Amount"
              name="unit_amount"
              type="number"
              value={form.unit_amount}
              onChange={handleChange}
              error={errors.unit_amount}
              required
            />
            <FormSelect
              label="Unit"
              name="unit_id"
              value={form.unit_id}
              onChange={handleChange}
              options={options.units.map((u) => ({ value: u.id, label: u.name }))}
              error={errors.unit_id}
              required
            />
            <FormInput
              label="Price per Unit"
              name="price_per_unit"
              type="number"
              value={form.price_per_unit}
              onChange={handleChange}
              error={errors.price_per_unit}
              required
            />
          </>
        )}

        <FormInput
          label="Subtotal"
          name="subtotal"
          type="number"
          value={isUnit ? unitSubtotal : form.subtotal}
          onChange={handleChange}
          error={errors.subtotal}
          disabled={isUnit}
          required={!isUnit}
        />

        <FormSelect
          label="Currency"
          name="currency_id"
          value={form.currency_id}
          onChange={handleChange}
          options={options.currencies.map((c) => ({
            value: c.id,
            label: `${c.currency?.code || "?"} (${c.currency?.symbol || "?"})`,
          }))}
          error={errors.currency_id}
          required
        />

        <FormSelect
          label="File"
          name="file_id"
          value={form.file_id}
          onChange={handleChange}
          options={options.files.map((f) => ({ value: f.id, label: f.original_file_name }))}
        />

        <div className={isUnit ? "" : "md:col-span-2"}>
          <FormTextarea
            label="Internal Note"
            name="internal_note"
            value={form.internal_note}
            onChange={handleChange}
            rows={3}
          />
        </div>
      </div>

      <span className="block text-gray-500 text-sm">
        Fields marked with <span className="text-danger-600">*</span> are mandatory.
      </span>

      {serverError && (
        <div className="bg-danger-100 text-danger-700 border border-danger-400 rounded p-3 text-center font-semibold">
          {serverError}
        </div>
      )}

      {success && (
        <div className="bg-green-100 text-green-700 border border-green-400 rounded p-3 text-center font-semibold">
          {success}
        </div>
      )}

      <div className="flex gap-4">
        <button
          type="submit"
          disabled={submitting}
          className={btn("primary", "sm")}
        >
          {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create"}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/quotation/${quotationId}?tab=finances`)}
          className={btn("secondary", "sm")}
        >
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <BackButton />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">{title}</h1>
      </div>

      {!isUnit ? (
        fields
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {fields}

          <div className="bg-white shadow rounded-lg p-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Relevant Prices</h2>
            <p className="text-sm text-gray-500 mb-3">
              Click a row to copy it into the form. Matching prices are highlighted in green.
            </p>

            {prices.length === 0 ? (
              <div className="text-center text-gray-500 py-8">No relevant prices found</div>
            ) : (
              <div className="border rounded-lg overflow-auto max-h-[520px]">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      {["Service", "Language Pair", "Specialization", "Unit", "Price per unit", "Currency", "Note"].map((h) => (
                        <th key={h} className="px-3 py-2 text-center font-semibold text-gray-700">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {prices.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => handlePriceSelect(row)}
                        className={`cursor-pointer border-t ${
                          selectedPriceId === row.id
                            ? "bg-green-200"
                            : row.isMatching
                            ? "bg-green-50 hover:bg-green-100"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-3 py-2">{row.service?.name || "—"}</td>
                        <td className="px-3 py-2">{formatLanguagePair(row.languagePair) || "—"}</td>
                        <td className="px-3 py-2">{row.specialization?.name || "—"}</td>
                        <td className="px-3 py-2">{row.unit || "—"}</td>
                        <td className="px-3 py-2 font-semibold">{row.price_per_unit}</td>
                        <td className="px-3 py-2">{row.currency?.currency?.code || "—"}</td>
                        <td className="px-3 py-2 truncate max-w-[150px]">{row.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationReceivablePage;
